import { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, Cell, Tooltip } from 'recharts';
import { IconTrendingUp, IconFileInvoice } from '@tabler/icons-react';
import { DashboardCardWrapper } from '../../commonComponents/DashboardCardWrapper';

const salesData = [
    { month: 'Jan', value: 1050 },
    { month: 'Feb', value: 1420 },
    { month: 'Mar', value: 1200 },
    { month: 'Apr', value: 1650 },
    { month: 'May', value: 1510 },
    { month: 'Jun', value: 1180 },
];

// CUSTOM CAPSULE BAR WITH INTEGRATED DOT & MONTH BADGE
const CustomCapsuleBar = (props) => {
    const { fill, x, y, width, height, payload, selectedMonth, onClick } = props;
    const isSelected = selectedMonth === payload.month;
    const badgeRadius = 19;
    const circleCenterX = x + width / 2;
    const circleCenterY = y + height - badgeRadius - 2;

    const capsuleColor = isSelected ? 'rgba(122, 58, 94, 0.08)' : 'rgba(241, 245, 249, 0.6)';
    const strokeColor = isSelected ? 'rgba(122, 58, 94, 0.3)' : 'transparent';
    const dotColor = isSelected ? '#7a3a5e' : '#a855f7';
    const badgeBg = isSelected ? '#7a3a5e' : '#ffffff';
    const badgeTextColor = isSelected ? '#ffffff' : '#475569';

    return (
        <g onClick={() => onClick(payload.month)} style={{ cursor: 'pointer' }}>
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                rx={width / 2}
                ry={width / 2}
                fill={capsuleColor}
                stroke={strokeColor}
                strokeWidth={1}
            />

            <line
                x1={circleCenterX}
                y1={y + 24}
                x2={circleCenterX}
                y2={circleCenterY - badgeRadius + 2}
                stroke="#cbd5e1"
                strokeWidth={1.5}
                strokeDasharray="3 3"
            />

            <circle
                cx={circleCenterX}
                cy={y + 18}
                r={4}
                fill={dotColor}
                style={{
                    filter: isSelected ? 'drop-shadow(0px 0px 4px rgba(122, 58, 94, 0.6))' : 'none'
                }}
            />

            <circle
                cx={circleCenterX}
                cy={circleCenterY}
                r={badgeRadius}
                fill={badgeBg}
                style={{
                    filter: isSelected
                        ? 'drop-shadow(0px 4px 8px rgba(122, 58, 94, 0.35))'
                        : 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.08))'
                }}
            />

            <text
                x={circleCenterX}
                y={circleCenterY}
                fill={badgeTextColor}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12}
                fontWeight={500}
            >
                {payload.month}
            </text>
        </g>
    );
};

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const item = payload[0].payload;
        return (
            <div
                className="bg-dark text-white rounded-pill px-2 py-1 shadow-sm fw-semibold position-relative"
                style={{ fontSize: '12px', whiteSpace: 'nowrap', transform: 'translateY(-10px)' }}
            >
                ${item.value.toLocaleString()}
                <div
                    className="position-absolute start-50 translate-middle-x"
                    style={{
                        bottom: '-4px',
                        width: 0,
                        height: 0,
                        borderLeft: '4px solid transparent',
                        borderRight: '4px solid transparent',
                        borderTop: '5px solid #0f172a'
                    }}
                />
            </div>
        );
    }
    return null;
};

export const InvoiceChart = () => {
    const [selectedMonth, setSelectedMonth] = useState('Apr');

    return (
        <DashboardCardWrapper
            headerText={"Average Sales"}
            headerSubtext={"Track your invoices."}
            headerIcon={<IconFileInvoice color='#7a3a5e' />}
            componentClassname={"w-100 position-relative"}
            componentStyles={{ flex: 1, height: '100%', overflow: "hidden" }}
        >
            <div className="row position-relative mt-2 ">
                <div className='col-4 d-flex flex-column justify-content-between' style={{ minHeight: '220px', }}>
                    <div>
                        <span className="d-flex align-items-center mb-0 pb-0 " style={{ fontWeight: 400, fontSize: '2em' }}>
                            $ 2,710.18
                        </span>

                        <div className="d-flex align-items-center gap-2 mb-2 mt-1">
                            <span
                                className="badge rounded-pill fw-semibold d-inline-flex align-items-center gap-1 px-2.5 py-1"
                                style={{ backgroundColor: '#e6f4ea', color: '#137333', fontSize: '11px' }}
                            >
                                + 4% <IconTrendingUp size={12} stroke={2.5} />
                            </span>
                            <span className="text-muted fw-medium" style={{ fontSize: '12px' }}>
                                47% of 30K goal met
                            </span>
                        </div>
                    </div>

                    <p className="text-muted mb-0 lh-sm" style={{ fontSize: '13.5px', maxWidth: '160px' }}>
                        This week revenue increased due to a 4.4 promo
                    </p>
                </div>

                <div className="col-8" style={{ height: '400px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={salesData}
                            margin={{ top: 35, right: 10, left: 10, bottom: 0 }}
                        >
                            <Tooltip
                                cursor={false}
                                content={<CustomTooltip />}
                            />
                            <Bar
                                dataKey="value"
                                barSize={48}
                                shape={(props) => (
                                    <CustomCapsuleBar
                                        {...props}
                                        selectedMonth={selectedMonth}
                                        onClick={(month) => setSelectedMonth(month)}
                                    />
                                )}
                            >
                                {salesData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </DashboardCardWrapper >
    );
};